// Realtime da caixa de entrada.
//
// É BROADCAST, não `postgres_changes`. Com postgres_changes o Realtime reavalia
// a RLS por assinante a cada linha, e uma caixa movimentada derruba o servidor.
// O worker publica o payload já montado em `inbox:org:{org_id}`, com dois
// eventos: `mensagem.recebida` (chegou do cliente) e `mensagem.enviada`
// (saiu daqui, inclusive de outro atendente na mesma conversa).
//
// ⚠️ O payload de `mensagem.recebida` é PARCIAL — traz o essencial para pintar
// o balão, não a linha inteira. Por isso o hook entrega o payload para render
// imediato E avisa que a lista precisa ser relida: quem manda no que aparece
// continua sendo o banco.

import { useEffect, useRef, useState } from 'react';
import { supabase } from '../../../src/lib/supabase';
import type { PayloadBroadcast } from './tipos';

export type EstadoRealtime = 'conectando' | 'ligado' | 'caiu';

interface Args {
  orgId: string | null;
  onEvento: (evento: 'recebida' | 'enviada', payload: PayloadBroadcast) => void;
}

export function useInboxRealtime({ orgId, onEvento }: Args): EstadoRealtime {
  const [estado, setEstado] = useState<EstadoRealtime>('conectando');
  // Ref mantém o callback atual sem recriar a assinatura a cada render — sem
  // isso o canal cairia e subiria de novo a cada digitação na busca.
  const cb = useRef(onEvento);
  useEffect(() => { cb.current = onEvento; }, [onEvento]);

  useEffect(() => {
    if (!orgId) return;

    const canal = supabase
      .channel(`inbox:org:${orgId}`)
      .on('broadcast', { event: 'mensagem.recebida' }, ({ payload }) => {
        cb.current('recebida', (payload ?? {}) as PayloadBroadcast);
      })
      .on('broadcast', { event: 'mensagem.enviada' }, ({ payload }) => {
        cb.current('enviada', (payload ?? {}) as PayloadBroadcast);
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') setEstado('ligado');
        else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') setEstado('caiu');
        else setEstado('conectando');
      });

    return () => {
      supabase.removeChannel(canal);
    };
  }, [orgId]);

  return estado;
}
